import React, { useEffect, useState } from 'react'

export interface AlertaPersonalizadaProps {
  tipo: 'success' | 'error' | 'confirmation' | 'warning' | 'info'
  titulo: string
  mensaje: string
  mostrar: boolean
  onConfirmar?: () => void
  onCancelar?: () => void
  onCerrar: () => void
  textoConfirmar?: string
  textoCancelar?: string
  autoClose?: boolean
  duracion?: number
}

const AlertaPersonalizada: React.FC<AlertaPersonalizadaProps> = ({
  tipo,
  titulo,
  mensaje,
  mostrar,
  onConfirmar,
  onCancelar,
  onCerrar,
  textoConfirmar = 'Confirmar',
  textoCancelar = 'Cancelar',
  autoClose = false,
  duracion = 3000
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (mostrar) {
      setIsVisible(true)
      setTimeout(() => setIsAnimating(true), 10)
    } else {
      setIsAnimating(false)
      setTimeout(() => setIsVisible(false), 300)
    }
  }, [mostrar])

  useEffect(() => {
    if (autoClose && mostrar && tipo !== 'confirmation') {
      const timer = setTimeout(() => {
        onCerrar()
      }, duracion)
      return () => clearTimeout(timer)
    }
  }, [autoClose, mostrar, duracion, onCerrar, tipo])

  if (!isVisible) return null

  const getColorClasses = () => {
    switch (tipo) {
      case 'success':
        return {
          bg: 'bg-gradient-to-br from-emerald-50 to-green-50',
          border: 'border-emerald-200',
          text: 'text-emerald-800',
          icon: 'text-emerald-500',
          button: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
        }
      case 'error':
        return {
          bg: 'bg-gradient-to-br from-red-50 to-rose-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: 'text-red-500',
          button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        }
      case 'warning':
        return {
          bg: 'bg-gradient-to-br from-amber-50 to-yellow-50',
          border: 'border-amber-200',
          text: 'text-amber-800',
          icon: 'text-amber-500',
          button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
        }
      case 'info':
        return {
          bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-500',
          button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
        }
      case 'confirmation':
        return {
          bg: 'bg-gradient-to-br from-slate-50 to-gray-50',
          border: 'border-slate-200',
          text: 'text-slate-800',
          icon: 'text-slate-500',
          button: 'bg-slate-600 hover:bg-slate-700 focus:ring-slate-500'
        }
      default:
        return {
          bg: 'bg-gradient-to-br from-gray-50 to-slate-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-500',
          button: 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500'
        }
    }
  }

  const getIcon = () => {
    const iconClass = "w-7 h-7 drop-shadow-sm"
    switch (tipo) {
      case 'success':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'error':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'warning':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        )
      case 'info':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'confirmation':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  const colors = getColorClasses()

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ease-out ${
        isAnimating ? 'bg-black bg-opacity-40 backdrop-blur-sm' : 'bg-black bg-opacity-0'
      }`}
      onClick={(e) => e.target === e.currentTarget && onCerrar()}
    >
      <div 
        className={`max-w-lg w-full mx-4 rounded-2xl border-2 shadow-2xl backdrop-blur-sm transition-all duration-300 ease-out transform ${
          isAnimating 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-95 opacity-0 translate-y-4'
        } ${colors.bg} ${colors.border}`}
      >
        {/* Header con icono y título */}
        <div className="flex items-start p-6 pb-4">
          <div className={`flex-shrink-0 ${colors.icon} mr-4 mt-1`}>
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-xl font-semibold leading-6 ${colors.text} mb-2`}>
              {titulo}
            </h3>
            <div className={`text-sm leading-relaxed ${colors.text} opacity-90`}>
              <p className="whitespace-pre-line">{mensaje}</p>
            </div>
          </div>
        </div>

        {/* Separador sutil */}
        <div className={`mx-6 border-t ${colors.border} opacity-30`}></div>

        {/* Botones de acción */}
        <div className="px-6 py-4 flex justify-end space-x-3">
          {tipo === 'confirmation' && onCancelar && (
            <button
              onClick={onCancelar}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 shadow-sm"
            >
              {textoCancelar}
            </button>
          )}
          {onConfirmar && (
            <button
              onClick={onConfirmar}
              className={`px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 shadow-lg ${colors.button}`}
            >
              {textoConfirmar}
            </button>
          )}
          {!onConfirmar && (
            <button
              onClick={onCerrar}
              className={`px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 shadow-lg ${colors.button}`}
            >
              Entendido
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AlertaPersonalizada